import type { Data } from '@dnd-kit/abstract'
import { SortableDraggable } from '@dnd-kit/dom/sortable'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { PencilSimpleIcon, PlusCircleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { ComponentProps, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetFolderContent } from '@/api/community-essentials'
import { useUpdateCommunityEssential } from '@/api/community-essentials/update-community-essential'
import { useUpdateFolder } from '@/api/community-essentials/update-folder'
import { TutorialModal } from '@/components/layout/modals/tutorial-modal'
import { Page } from '@/components/layout/page'
import { TutorialBanner } from '@/components/layout/tutorial-banner'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { RadialLoader } from '@/components/ui/loaders/radial-loader'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import type { CommunityEssentialModel } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import { Breadcrumbs } from './breadcrumbs/breadcrumbs'
import { MAX_FOLDER_DEPTH, MIN_PATH_LENGTH_FOR_PARENT } from './community-essentials.constants'
import { useFolderNavigation } from './community-essentials.hooks'
import * as S from './community-essentials.styled'
import { DeleteFolderDialog } from './delete-folder-dialog/delete-folder-dialog'
import { EmptyState } from './empty-state/empty-state'
import { EssentialFormModal } from './essential-form-modal/essential-form-modal'
import { EssentialItem } from './essential-item/essential-item'
import { FolderFormModal } from './folder-form-modal/folder-form-modal'
import { FolderItem } from './folder-item/folder-item'

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>
type DragOverHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragOver']>

export const AdminCommunityEssentialsPage = () => {
  const { t } = useTranslation(['community-essentials', 'common'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id

  const {
    folderPath,
    currentFolder,
    isRoot,
    depth,
    navigateToFolder,
    navigateBack,
    navigateToBreadcrumb,
    updateCurrentFolderName,
  } = useFolderNavigation()

  const { data, isError, isPlaceholderData } = useGetFolderContent({
    buildingId,
    folderId: currentFolder.id,
  })
  const { essentials = [], folders = [] } = data ?? {}

  const { mutateAsync: updateEssential } = useUpdateCommunityEssential()
  const { mutateAsync: updateFolderMutation } = useUpdateFolder()

  const [dropTargetFolderId, setDropTargetFolderId] = useState<number | null>(null)

  const showAddFolder = depth < MAX_FOLDER_DEPTH
  const showFolderActions = !isRoot
  const showTutorialBanner = isRoot && essentials.length > 0 && folders.length === 0
  const hasContent = folders.length > 0 || essentials.length > 0

  const handleAddEssential = () => {
    if (!buildingId) return
    popupFactory.open(EssentialFormModal, {
      buildingId,
      currentFolderId: isRoot ? null : currentFolder.id,
      essentialToEdit: null,
    })
  }

  const handleEditEssential = (item: CommunityEssentialModel) => {
    if (!buildingId) return
    popupFactory.open(EssentialFormModal, {
      buildingId,
      currentFolderId: isRoot ? null : currentFolder.id,
      essentialToEdit: item,
    })
  }

  const handleAddFolder = () => {
    if (!buildingId) return
    popupFactory.open(FolderFormModal, {
      buildingId,
      mode: 'create',
      parentFolderId: isRoot ? null : currentFolder.id,
    } as ComponentProps<typeof FolderFormModal>)
  }

  const handleWatchTutorial = () => {
    popupFactory.open(TutorialModal, {
      content: {
        bullets: [
          t('community-essentials:tutorial.modal.bullet1'),
          t('community-essentials:tutorial.modal.bullet2'),
          t('community-essentials:tutorial.modal.bullet3'),
        ],
        description: t('community-essentials:tutorial.modal.description'),
        footer: (
          <Button
            size="medium"
            use="upsell"
            onClick={() => {
              popupFactory.close(TutorialModal)
              handleAddFolder()
            }}
          >
            {t('community-essentials:addFolder')}
            <Icon icon={PlusCircleIcon} size="2rem" />
          </Button>
        ),
        title: t('community-essentials:tutorial.modal.title'),
      },
      video: {
        alt: t('community-essentials:tutorial.modal.videoAlt'),
        previewUrl: '/images/upselling/essentials-folders.png',
        url: 'https://redvike-chefs.s3.us-east-1.amazonaws.com/community-folders.mp4',
      },
    })
  }

  const handleEditFolder = () => {
    if (isRoot || !buildingId) return
    const parentId =
      folderPath.length >= MIN_PATH_LENGTH_FOR_PARENT ? folderPath[folderPath.length - 2].id : 0
    popupFactory.open(FolderFormModal, {
      buildingId,
      folder: {
        buildingId,
        createdAt: 0,
        id: currentFolder.id,
        name: currentFolder.name,
        nestedLevel: depth,
        parentFolderId: parentId === 0 ? null : parentId,
        position: 0,
        updatedAt: 0,
      },
      mode: 'edit',
      onSuccess: (name: string) => {
        updateCurrentFolderName(name)
      },
    } as ComponentProps<typeof FolderFormModal>)
  }

  const handleDeleteFolder = () => {
    if (isRoot || !buildingId) return
    const parentId =
      folderPath.length >= MIN_PATH_LENGTH_FOR_PARENT ? folderPath[folderPath.length - 2].id : 0
    popupFactory.open(DeleteFolderDialog, {
      folder: {
        buildingId,
        createdAt: 0,
        id: currentFolder.id,
        name: currentFolder.name,
        nestedLevel: depth,
        parentFolderId: parentId === 0 ? null : parentId,
        position: 0,
        updatedAt: 0,
      },
      onDeleted: () => {
        popupFactory.close(DeleteFolderDialog)
        navigateBack()
      },
    })
  }

  const handleDragOver: DragOverHandler = useCallback((event) => {
    const { operation } = event
    const { source, target } = operation
    if (!source || !target) {
      setDropTargetFolderId(null)
      return
    }

    const sourceId = String(source.id)
    const targetId = String(target.id)
    const isSourceEssential = sourceId.startsWith('essential-')
    const isSourceFolder = sourceId.startsWith('folder-')

    if (isSourceEssential) {
      const isOverFolder = targetId.startsWith('drop-folder-') || targetId.startsWith('folder-')
      if (isOverFolder) {
        const folderId = targetId.startsWith('drop-folder-')
          ? Number(targetId.replace('drop-folder-', ''))
          : Number(targetId.replace('folder-', ''))
        setDropTargetFolderId(folderId)
        event.preventDefault()
        return
      }

      setDropTargetFolderId(null)
      return
    }

    setDropTargetFolderId(null)

    if (isSourceFolder) {
      const isTargetFolder = targetId.startsWith('folder-')
      if (!isTargetFolder) {
        event.preventDefault()
      }
    }
  }, [])

  const handleDragEnd: DragEndHandler = async (event) => {
    const hoveredFolderId = dropTargetFolderId
    setDropTargetFolderId(null)

    const { canceled, operation } = event
    if (canceled || !buildingId) return

    const { source } = operation
    if (!source) return

    const sourceId = String(source.id)
    const isSourceFolder = sourceId.startsWith('folder-')

    try {
      if (!isSourceFolder && hoveredFolderId !== null) {
        const essentialId = Number(sourceId.replace('essential-', ''))
        await updateEssential({ buildingId, folderId: hoveredFolderId, id: essentialId })
        return
      }

      if (!isSortable(source)) return
      const sortableSource = source as SortableDraggable<Data>
      const sourceIndex = sortableSource.initialIndex
      const targetIndex = sortableSource.index
      if (sourceIndex === targetIndex) return

      if (isSourceFolder) {
        if (targetIndex >= folders.length) return
        const sourceFolderId = Number(sourceId.replace('folder-', ''))
        const sourceFolder = folders.find((f) => f.id === sourceFolderId)
        const targetFolder = folders[targetIndex]
        if (!sourceFolder || !targetFolder) return
        await updateFolderMutation({
          folderId: sourceFolder.id,
          name: sourceFolder.name,
          parentFolderId: sourceFolder.parentFolderId,
          position: targetFolder.position,
        })
      } else {
        if (targetIndex < folders.length) return
        const essentialId = Number(sourceId.replace('essential-', ''))
        const essentialTargetIndex = targetIndex - folders.length
        const targetEssential = essentials[essentialTargetIndex]
        if (!targetEssential) return
        await updateEssential({ buildingId, id: essentialId, position: targetEssential.position })
      }
    } catch (err) {
      toastFactory.error(getApiErrorMessage(err as Error))
    }
  }

  if (isPlaceholderData) {
    return (
      <Page>
        <S.Toolbar>
          <Typography size="heading-large" weight="semibold">
            {t('community-essentials:title')}
          </Typography>
        </S.Toolbar>
        <S.LoaderWrapper>
          <RadialLoader />
        </S.LoaderWrapper>
      </Page>
    )
  }

  if (isError) {
    return (
      <Page>
        <S.Toolbar>
          <Typography size="heading-large" weight="semibold">
            {t('community-essentials:title')}
          </Typography>
        </S.Toolbar>
        <Typography color="red500">{t('community-essentials:loadError')}</Typography>
      </Page>
    )
  }

  return (
    <Page>
      <S.Toolbar>
        {isRoot ? (
          <Typography size="heading-large" weight="semibold">
            {t('community-essentials:title')}
          </Typography>
        ) : (
          <Breadcrumbs
            folderPath={folderPath}
            onBack={navigateBack}
            onNavigate={navigateToBreadcrumb}
          />
        )}
        <S.ButtonsWrapper>
          {showAddFolder && (
            <Button use="secondary" size="small" onClick={handleAddFolder}>
              {t('community-essentials:addFolder')}
              <Icon icon={PlusIcon} size="1.6rem" />
            </Button>
          )}
          <Button use="secondary" size="small" onClick={handleAddEssential}>
            {t('community-essentials:addEssential')}
            <Icon icon={PlusIcon} size="1.6rem" />
          </Button>
          {showFolderActions && (
            <>
              <Button use="secondary" size="small" onClick={handleEditFolder}>
                {t('community-essentials:edit')}
                <Icon icon={PencilSimpleIcon} size="1.6rem" />
              </Button>
              <Button use="secondary" size="small" onClick={handleDeleteFolder}>
                {t('common:actions.delete')}
                <Icon icon={TrashIcon} size="1.6rem" />
              </Button>
            </>
          )}
        </S.ButtonsWrapper>
      </S.Toolbar>

      {showTutorialBanner && (
        <TutorialBanner
          description={t('community-essentials:tutorial.banner.description')}
          title={t('community-essentials:tutorial.banner.title')}
          onButtonClick={handleWatchTutorial}
        />
      )}

      {hasContent ? (
        <DragDropProvider onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
          <S.EssentialsList>
            {folders.map((folder, index) => (
              <FolderItem
                key={folder.id}
                folder={folder}
                index={index}
                isDropTarget={dropTargetFolderId === folder.id}
                onNavigate={navigateToFolder}
              />
            ))}
            {essentials.map((item, index) => (
              <EssentialItem
                key={item.id}
                index={folders.length + index}
                item={item}
                onEdit={handleEditEssential}
              />
            ))}
          </S.EssentialsList>
        </DragDropProvider>
      ) : (
        <EmptyState insideFolder={!isRoot} onAddEssential={handleAddEssential} />
      )}
    </Page>
  )
}
